import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

const DAYS = ['Minggu','Senin','Selasa','Rabu','Kamis','Jumat','Sabtu'];
const inputStyle = { border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 12px', fontSize: '.86rem', outline: 'none' };

function Section({ title, subtitle, children }) {
  return (
    <div style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '20px 22px', marginBottom: 16, boxShadow: 'var(--shadow)' }}>
      <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.05rem', marginBottom: 2 }}>{title}</div>
      {subtitle && <div style={{ fontSize: '.78rem', color: 'var(--muted)', marginBottom: 14 }}>{subtitle}</div>}
      <div style={{ marginTop: subtitle ? 0 : 14 }}>{children}</div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F5EEE6' }}>
      <div>
        <div style={{ fontSize: '.84rem' }}>{label}</div>
        {hint && <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: 1 }}>{hint}</div>}
      </div>
      <div style={{ flexShrink: 0, marginLeft: 16 }}>{children}</div>
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [saved, setSaved]       = useState(false);
  const [loading, setLoading]   = useState(true);

  useEffect(() => { load(); }, []);

  async function load() {
    const { data } = await supabase.from('app_settings').select('key,value');
    const map = {};
    (data || []).forEach(r => map[r.key] = r.value);
    setSettings(map);
    setLoading(false);
  }

  const get = (k, def = '') => settings[k] ?? def;
  const set = (k, v) => setSettings(s => ({ ...s, [k]: String(v) }));

  async function handleSave() {
    const upserts = Object.entries(settings).map(([key, value]) => ({ key, value }));
    await supabase.from('app_settings').upsert(upserts, { onConflict: 'key' });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  function useMyLocation() {
    if (!navigator.geolocation) { alert('Browser tidak mendukung akses lokasi'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set('outlet_lat', pos.coords.latitude.toFixed(6));
        set('outlet_lng', pos.coords.longitude.toFixed(6));
      },
      () => alert('Gagal mengambil lokasi. Pastikan izin lokasi browser sudah diaktifkan.'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  const doubleDays = (get('double_point_days') || '').split(',').filter(Boolean).map(Number);
  const toggleDay  = (idx) => {
    const cur = doubleDays.includes(idx) ? doubleDays.filter(d => d !== idx) : [...doubleDays, idx];
    set('double_point_days', cur.sort().join(','));
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Memuat...</div>;

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px', maxWidth: 680 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem' }}>Settings</div>
          <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>Konfigurasi loyalty & reward</div>
        </div>
        <button onClick={handleSave} style={{ padding: '9px 20px', borderRadius: 8, background: saved ? '#1B5E20' : '#CD5226', color: saved ? 'white' : '#fff', border: 'none', fontSize: '.84rem', fontFamily: "'DM Sans',sans-serif", cursor: 'pointer', transition: 'background .2s' }}>
          {saved ? '✓ Tersimpan' : 'Simpan Semua'}
        </button>
      </div>

      {/* Loyalty On/Off */}
      <Section title="Program Loyalty" subtitle="Aktifkan sistem poin untuk semua transaksi POS dan PO">
        <Row label="Aktifkan Loyalty" hint="Jika dimatikan, poin tidak dihitung">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={get('loyalty_enabled') === '1'} onChange={e => set('loyalty_enabled', e.target.checked ? '1' : '0')} style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: '.82rem' }}>{get('loyalty_enabled') === '1' ? 'Aktif' : 'Nonaktif'}</span>
          </label>
        </Row>
      </Section>

      {/* Earn & Redeem */}
      <Section title="Poin Earn & Redeem" subtitle="Aturan perolehan dan penukaran poin">
        <Row label="Poin per Rupiah" hint="Contoh: 0.001 = Rp1.000 → 1 poin">
          <input type="number" step="0.0001" value={get('points_per_rupiah', '0.001')} onChange={e => set('points_per_rupiah', e.target.value)} style={{ ...inputStyle, width: 100, textAlign: 'right' }} />
        </Row>
        <Row label="Unit Redeem" hint="Setiap N poin = Rp M diskon">
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="number" value={get('redeem_points_unit', '50')} onChange={e => set('redeem_points_unit', e.target.value)} style={{ ...inputStyle, width: 70, textAlign: 'right' }} />
            <span style={{ fontSize: '.8rem', color: 'var(--muted)' }}>poin =</span>
            <input type="number" value={get('redeem_rupiah_unit', '1000')} onChange={e => set('redeem_rupiah_unit', e.target.value)} style={{ ...inputStyle, width: 90, textAlign: 'right' }} />
            <span style={{ fontSize: '.8rem', color: 'var(--muted)' }}>Rp</span>
          </div>
        </Row>
        <Row label="Bonus Welcome" hint="Poin gratis untuk customer baru">
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="number" value={get('welcome_bonus', '10')} onChange={e => set('welcome_bonus', e.target.value)} style={{ ...inputStyle, width: 80, textAlign: 'right' }} />
            <span style={{ fontSize: '.8rem', color: 'var(--muted)' }}>poin</span>
          </div>
        </Row>
        <Row label="Diskon Birthday" hint="Nominal diskon khusus di hari ulang tahun">
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: '.8rem', color: 'var(--muted)' }}>Rp</span>
            <input type="number" value={get('birthday_discount', '5000')} onChange={e => set('birthday_discount', e.target.value)} style={{ ...inputStyle, width: 100, textAlign: 'right' }} />
          </div>
        </Row>
        <Row label="Expire Poin" hint="Poin hangus otomatis jika tidak ada transaksi selama X hari. Isi 0 untuk nonaktifkan.">
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="number" min="0" value={get('points_expire_days', '0')} onChange={e => set('points_expire_days', e.target.value)} style={{ ...inputStyle, width: 80, textAlign: 'right' }} />
            <span style={{ fontSize: '.8rem', color: 'var(--muted)' }}>hari</span>
          </div>
        </Row>
      </Section>

      {/* Double point days */}
      <Section title="Double Point Days" subtitle="Hari di mana poin dikalikan 2×">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 6 }}>
          {DAYS.map((d, i) => (
            <button key={i} type="button" onClick={() => toggleDay(i)}
              style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border)', background: doubleDays.includes(i) ? '#CD5226' : 'white', color: doubleDays.includes(i) ? '#fff' : 'var(--muted)', fontSize: '.78rem', cursor: 'pointer' }}>
              {d}
            </button>
          ))}
        </div>
        <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: 10 }}>
          {doubleDays.length === 0 ? 'Tidak ada double point day' : `Double point: ${doubleDays.map(d => DAYS[d]).join(', ')}`}
        </div>
      </Section>

      {/* Lokasi Outlet & Radius Delivery */}
      <Section title="Lokasi Outlet & Radius Delivery" subtitle="Dipakai untuk cek jarak otomatis saat customer pilih Diantar di web">
        <div style={{ padding: '10px 0' }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '.76rem', color: 'var(--muted)', marginBottom: 6 }}>Latitude Outlet</label>
              <input type="text" placeholder="-7.257472" value={get('outlet_lat')} onChange={e => set('outlet_lat', e.target.value)}
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '.76rem', color: 'var(--muted)', marginBottom: 6 }}>Longitude Outlet</label>
              <input type="text" placeholder="112.752090" value={get('outlet_lng')} onChange={e => set('outlet_lng', e.target.value)}
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
            </div>
          </div>
          <button type="button" onClick={useMyLocation} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'white', color: 'var(--ink)', fontSize: '.78rem', cursor: 'pointer', marginBottom: 14 }}>
            📍 Pakai Lokasi Saya Sekarang (kalau lagi di outlet)
          </button>

          <Row label="Radius Maksimal Delivery" hint="Order dengan jarak lebih dari ini akan ditolak sistem di web">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="number" step="0.1" min="0" value={get('delivery_radius_km', '3')} onChange={e => set('delivery_radius_km', e.target.value)} style={{ ...inputStyle, width: 80, textAlign: 'right' }} />
              <span style={{ fontSize: '.8rem', color: 'var(--muted)' }}>km</span>
            </div>
          </Row>

          <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: 10 }}>
            Kalau latitude/longitude belum diisi, pengecekan jarak otomatis akan dilewati (order delivery tetap bisa masuk, dikonfirmasi manual via WhatsApp).
          </div>
        </div>
      </Section>

      {/* Notifikasi WA Dapur */}
      <Section title="Notifikasi Order Online" subtitle="Nomor WhatsApp yang menerima notifikasi tiap ada order menu POS baru dari web">
        <div style={{ padding: '10px 0' }}>
          <label style={{ display: 'block', fontSize: '.76rem', color: 'var(--muted)', marginBottom: 6 }}>
            Nomor WA Staf Dapur <span style={{ color: '#AAA' }}>(pisahkan dengan koma kalau lebih dari satu)</span>
          </label>
          <input
            type="text"
            placeholder="08123456789, 08987654321"
            value={get('kitchen_wa_numbers')}
            onChange={e => set('kitchen_wa_numbers', e.target.value)}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
          />
          <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: 8 }}>
            Setiap ada pesanan menu POS baru lewat web, ringkasan order dikirim otomatis via WhatsApp ke semua nomor di atas.
          </div>
        </div>
      </Section>

      {/* Tier thresholds */}
      <Section title="Threshold Tier Loyalty" subtitle="Minimum lifetime poin untuk naik tier">
        {[['tier_langganan_min', 'Langganan', 'Tier kedua'], ['tier_sahabat_min', 'Sahabat', 'Tier ketiga'], ['tier_keluarga_min', 'Keluarga', 'Tier tertinggi']].map(([k, tier, desc]) => (
          <Row key={k} label={tier} hint={desc}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="number" value={get(k)} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, width: 90, textAlign: 'right' }} />
              <span style={{ fontSize: '.8rem', color: 'var(--muted)' }}>poin</span>
            </div>
          </Row>
        ))}
        <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginTop: 12, padding: '8px 10px', background: 'var(--cream)', borderRadius: 6 }}>
          Kenalan → {get('tier_langganan_min')} poin → Langganan → {get('tier_sahabat_min')} poin → Sahabat → {get('tier_keluarga_min')} poin → Keluarga
        </div>
      </Section>
    </div>
  );
}
